// Client wrappers for /api/shares (user-targeted shares — distinct from
// the anonymous share-link UI in components/ItemDetail.tsx's existing
// ShareSection, which still hits /api/share-links).

export type SharePermission = "read" | "read_tick" | "read_comment" | "edit";
export type ShareScope = "group" | "item" | "excalidraw";

export type UserBrief = {
  id: string;
  email: string;
  handle: string | null;
  display_name: string | null;
};

export type ShareRow = {
  id: string;
  direction: "incoming" | "outgoing";
  scope: ShareScope;
  scopeId: string;
  includeSubtree: boolean;
  permissions: SharePermission;
  createdAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  scopeTitle: string | null;
  /** For outgoing-to-email-not-yet-user: `{email, pending: true}`.
   *  For resolved users: the full UserBrief.  Null only in odd states. */
  other: (UserBrief | { email: string; pending: true }) | null;
};

export type CreateShareResponse =
  | { id: string; status: "pending"; deliveredTo: "user" }
  | {
      id: string;
      status: "invited";
      deliveredTo: "email";
      delivery: { sent: boolean; reason?: string };
    };

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

export async function createShare(
  token: string,
  body: {
    recipient: string;
    scope: ShareScope;
    scopeId: string;
    includeSubtree?: boolean;
    permissions?: "read" | "edit";
  },
): Promise<CreateShareResponse> {
  const r = await fetch("/api/shares", {
    method: "POST",
    headers: { ...authHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await r.json().catch(() => null)) as
    | CreateShareResponse
    | { error: string }
    | null;
  if (!r.ok) {
    const code = data && "error" in data ? data.error : `HTTP ${r.status}`;
    throw new Error(code ?? "request_failed");
  }
  return data as CreateShareResponse;
}

export async function listShares(
  token: string,
  direction: "incoming" | "outgoing",
): Promise<ShareRow[]> {
  const r = await fetch(`/api/shares?direction=${direction}`, {
    headers: authHeaders(token),
  });
  if (!r.ok) throw new Error(`shares ${r.status}`);
  return (await r.json()) as ShareRow[];
}

export async function decideShare(
  token: string,
  id: string,
  action: "accept" | "decline",
): Promise<void> {
  const r = await fetch(
    `/api/shares/${encodeURIComponent(id)}/${action}`,
    { method: "POST", headers: authHeaders(token) },
  );
  if (!r.ok) {
    const body = (await r.json().catch(() => null)) as
      | { error?: string }
      | null;
    throw new Error(body?.error ?? `HTTP ${r.status}`);
  }
}

export async function revokeShare(token: string, id: string): Promise<void> {
  const r = await fetch(`/api/shares/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!r.ok) throw new Error(`revoke ${r.status}`);
}

export async function updateSharePermissions(
  token: string,
  id: string,
  permissions: SharePermission,
): Promise<void> {
  const r = await fetch(`/api/shares/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { ...authHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ permissions }),
  });
  if (!r.ok) {
    const resp = (await r.json().catch(() => null)) as
      | { error?: string }
      | null;
    throw new Error(resp?.error ?? `HTTP ${r.status}`);
  }
}

// ---------------------------------------------------------------------------
// Per-scope activity-notification watch.  The flag is keyed on
// (current user, scope, scopeId) — not per-share — so one toggle covers
// every recipient of the same group/item.

export async function getShareWatch(
  token: string,
  scope: ShareScope,
  scopeId: string,
): Promise<boolean> {
  const r = await fetch(
    `/api/share-watches/${scope}/${encodeURIComponent(scopeId)}`,
    { headers: authHeaders(token) },
  );
  if (!r.ok) throw new Error(`watch ${r.status}`);
  const body = (await r.json()) as { watching: boolean };
  return body.watching;
}

export async function setShareWatch(
  token: string,
  scope: ShareScope,
  scopeId: string,
  watching: boolean,
): Promise<void> {
  const r = await fetch(
    `/api/share-watches/${scope}/${encodeURIComponent(scopeId)}`,
    {
      method: "PUT",
      headers: { ...authHeaders(token), "Content-Type": "application/json" },
      body: JSON.stringify({ watching }),
    },
  );
  if (!r.ok) throw new Error(`watch ${r.status}`);
}

export async function fetchShareSnapshot(
  token: string,
  id: string,
): Promise<unknown> {
  const r = await fetch(`/api/shares/${encodeURIComponent(id)}`, {
    headers: authHeaders(token),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => null)) as
      | { error?: string }
      | null;
    throw new Error(body?.error ?? `HTTP ${r.status}`);
  }
  return await r.json();
}

export function shareErrorMessage(code: string): string {
  switch (code) {
    case "user_not_found":
      return "No user with that handle or email. (An email will be invited.)";
    case "self_target":
      return "You can't share with yourself.";
    case "scope_not_owned":
      return "You don't own that item or group.";
    case "share_already_exists":
      return "You've already shared this with that person — revoke the existing share first.";
    case "already_accepted":
      return "Already accepted.";
    case "revoked":
      return "That share was revoked.";
    case "not_accepted":
      return "You need to accept this share before viewing it.";
    case "forbidden":
      return "Not authorised.";
    case "not_found":
      return "That share no longer exists.";
    default:
      return code;
  }
}
